/**
 * Per-PRINT quantity editor (round 9) — the sibling of PhotoQtyDialog. Where
 * that dialog lists the PHOTOS inside a group line, this one lists the PRINTS
 * of ONE photo: whenever a photo-level qty control covers more than one
 * distinct print (different size / paper / border / frame), tapping it opens
 * this compact prompt — one row per print (small preview chip + size/paper
 * label + stepper) — so the customer says exactly which print to add or
 * remove. Used by the Step-2 Order pane photo rows and Step-3 cart lines
 * whose single photo carries several prints; single-print photos keep their
 * direct steppers.
 *
 * Drafts commit on "Update quantities" (Esc/Cancel discards): patches come
 * from cartModel.perPrintQtyPlan — a print drafted to 0 is REMOVED on commit
 * (rows hint "removed" live). The photo must keep ≥ 1 print overall (an
 * all-zero draft can't commit — remove the whole row with ✕ instead).
 */

import type { PricingData } from '@rpl/pricing';
import { useState } from 'react';
import type { UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { ModalShell } from '../sizepaper/ModalShell';
import { printLineLabel } from '../sizepaper/photoOrder';
import { QtyStepper } from '../sizepaper/QtyStepper';
import { perPrintQtyPlan } from './cartModel';

export interface PrintQtyDialogProps {
  pricing: PricingData;
  /** ONE photo's member prints (already >1, or this dialog wouldn't open). */
  members: readonly UiPrint[];
  /** The photo the prints belong to (preview chip + subtitle). */
  photo: { previewUrl: string | null; filename: string } | undefined;
  onClose: () => void;
}

export function PrintQtyDialog({ pricing, members, photo, onClose }: PrintQtyDialogProps) {
  const updatePrint = useStore((s) => s.updatePrint);
  const removePrint = useStore((s) => s.removePrint);

  // Row skeleton is frozen at open (the member prints); drafts layer on top
  // so rows never jump or vanish mid-edit.
  const [drafts, setDrafts] = useState<ReadonlyMap<string, number>>(
    () => new Map(members.map((m) => [m.id, m.qty])),
  );

  const draftOf = (m: UiPrint) => drafts.get(m.id) ?? m.qty;
  const total = members.reduce((a, m) => a + draftOf(m), 0);
  const dirty = members.some((m) => draftOf(m) !== m.qty);

  const setDraft = (printId: string, qty: number) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(printId, qty);
      return next;
    });
  };

  const commit = () => {
    if (total < 1) return;
    for (const patch of perPrintQtyPlan(members, drafts)) {
      if (patch.qty <= 0) removePrint(patch.printUiId);
      else updatePrint(patch.printUiId, { qty: patch.qty });
    }
    onClose();
  };

  return (
    <ModalShell onClose={onClose} width={460} label="Quantities per print" className="pqty">
      <div className="pqty__head">
        <div>
          <div className="pqty__title">Which print?</div>
          <div className="pqty__sub">{photo?.filename ?? 'This photo'} has several prints — pick the one to change.</div>
        </div>
        <button type="button" className="pqty__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="pqty__rows">
        {members.map((m) => {
          const qty = draftOf(m);
          const label = printLineLabel(pricing, m);
          return (
            <div key={m.id} className="pqty__row">
              {photo?.previewUrl ? (
                <img className="pqty__thumb" src={photo.previewUrl} alt="" loading="lazy" />
              ) : (
                <span className="pqty__thumb pqty__thumb--ph" />
              )}
              <span className="pqty__name" title={label}>
                {label}
                {qty === 0 && <em className="pqty__gone">removed when you update</em>}
              </span>
              <QtyStepper
                variant="light"
                ariaLabel={`Quantity for ${label}`}
                value={qty}
                min={0}
                onChange={(next) => setDraft(m.id, next)}
              />
            </div>
          );
        })}
      </div>

      <div className="pqty__foot">
        <div className="pqty__totals">
          <span className="pqty__total" aria-live="polite">
            Total: <strong>{total}</strong> print{total === 1 ? '' : 's'}
          </span>
        </div>
        {total < 1 && (
          <span className="pqty__min">Keep at least 1 print — or remove the whole row with ✕.</span>
        )}
        <div className="pqty__actions">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={total < 1 || !dirty}
            onClick={commit}
          >
            Update quantities
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
